import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { PageLoader } from '@/components/PageLoader';
import { drawOnCanvas, drawRectangle } from '@/utils/drawCanvas';
import Rectangle from '@/components/Rectangle';
import { clearCanvas, getCurrentPageData } from '@/globals/global';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { InputNumber } from 'primereact/inputnumber';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

interface PDFViewerProps {
  file: string | null;
  fileData: any;
  pdfWidth: number;
  pdfHeight: number;
  pageNumber: number;
  setPageNumber: (page: number) => void;
  isDrawingMode: boolean;
  highlightedPinId: string | null;
  fileId: string;
  onPageChange: (newPage: number) => Promise<void>;
  initialPageNumber: number;
  navigate: (options: { to: string; search: { page: number } }) => void;
  onDrawingComplete?: (
    boundingBox: {
      leftmost: number;
      rightmost: number;
      topmost: number;
      bottommost: number;
    },
    pinUid: string | null
  ) => Promise<void>;
  currentDrawingPin: string | null;
}

const PDFViewer: FC<PDFViewerProps> = ({
  file,
  fileData,
  pdfWidth,
  pdfHeight,
  pageNumber,
  setPageNumber,
  isDrawingMode,
  highlightedPinId,
  fileId,
  onPageChange,
  initialPageNumber,
  navigate,
  onDrawingComplete,
  currentDrawingPin,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1.0);
  const [rectangle, setRectangle] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pinHasNoBoundingBox, setPinHasNoBoundingBox] = useState(false);

  useEffect(() => {
    if (numPages) {
      if (pageNumber < 1) {
        setPageNumber(1);
        navigate({
          to: `/file/${fileId}`,
          search: { page: 1 },
        });
      } else if (pageNumber > numPages) {
        setPageNumber(1);
        navigate({
          to: `/file/${fileId}`,
          search: { page: 1 },
        });
      } else if (pageNumber !== initialPageNumber && initialPageNumber >= 1 && initialPageNumber <= numPages) {
        setPageNumber(initialPageNumber);
      }
    }
  }, [initialPageNumber, numPages, pageNumber, navigate, fileId, setPageNumber]);

  const updateCanvas = useCallback(() => {
    const pageData = getCurrentPageData(fileData, pageNumber);
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (canvas && context && pageData) {
      context.clearRect(0, 0, canvas.width, canvas.height);

      if (pageData.status === 'success') {
        context.save();
        context.scale(scale, scale);
        drawOnCanvas(pageData, pdfWidth, canvasRef);

        /**
         * If we have highlighted pin, draw a rectangle around it
         */
        if (highlightedPinId) {
          const pinData = canvas.dataset[`pinId_${highlightedPinId}`];
          if (pinData) {
            const { x, y, width, height } = JSON.parse(pinData);
            context.strokeStyle = 'rgb(183,0,255)';
            context.lineWidth = 3 / scale;
            context.strokeRect(x, y, width, height);
          }
        }

        if (rectangle) {
          drawRectangle(context, rectangle.x, rectangle.y, rectangle.width, rectangle.height);
        }

        context.restore();
      }
    }
  }, [fileData, pageNumber, scale, highlightedPinId, rectangle, pdfWidth]);

  useEffect(() => {
    updateCanvas();
  }, [updateCanvas]);

  useEffect(() => {
    if (!isDrawingMode && rectangle && onDrawingComplete) {
      const pageData = getCurrentPageData(fileData, pageNumber);

      if (pageData && pageData.width) {
        // Calculate the same scaling factor used in drawOnCanvas
        const scaleFactor = pdfWidth / pageData.width;

        // Convert coordinates back to original PDF scale
        const boundingBox = {
          leftmost: Math.round(rectangle.x / scaleFactor),
          rightmost: Math.round((rectangle.x + rectangle.width) / scaleFactor),
          topmost: Math.round(rectangle.y / scaleFactor),
          bottommost: Math.round((rectangle.y + rectangle.height) / scaleFactor),
        };

        handleDrawingComplete(boundingBox);
      }
    }
  }, [isDrawingMode, rectangle, onDrawingComplete, fileData, pageNumber, pdfWidth]);

  /**
   * Load existing pin bounding box when entering drawing mode
   */
  useEffect(() => {
    if (isDrawingMode && currentDrawingPin && fileData) {
      const pageData = getCurrentPageData(fileData, pageNumber);
      if (pageData && pageData.switchboards) {
        // Find the pin in the data
        let foundPin = null;
        for (const switchboard of pageData.switchboards) {
          if (switchboard.pins) {
            foundPin = switchboard.pins.find((pin: any) => pin.uid === currentDrawingPin);
            if (foundPin) break;
          }
        }

        // If we found the pin and it has valid bbox coordinates
        if (
          foundPin &&
          'bbox_leftmost' in foundPin &&
          'bbox_topmost' in foundPin &&
          'bbox_rightmost' in foundPin &&
          'bbox_bottommost' in foundPin &&
          foundPin.bbox_rightmost > foundPin.bbox_leftmost &&
          foundPin.bbox_bottommost > foundPin.bbox_topmost
        ) {
          // Calculate the scaling factor
          const scaleFactor = pdfWidth / pageData.width;

          // Set the rectangle with the existing coordinates
          setRectangle({
            x: foundPin.bbox_leftmost * scaleFactor,
            y: foundPin.bbox_topmost * scaleFactor,
            width: (foundPin.bbox_rightmost - foundPin.bbox_leftmost) * scaleFactor,
            height: (foundPin.bbox_bottommost - foundPin.bbox_topmost) * scaleFactor,
          });
          setPinHasNoBoundingBox(false);
        } else if (foundPin) {
          setPinHasNoBoundingBox(true);
          setRectangle(null);
        }
      }
    } else if (!isDrawingMode) {
      // Clear the rectangle when exiting drawing mode
      setRectangle(null);
      setPinHasNoBoundingBox(false);
    }
  }, [isDrawingMode, currentDrawingPin, fileData, pageNumber, pdfWidth]);

  /**
   * Handle PDF clicking event for creating rectangle or edit ones
   */
  const handlePdfClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const x = (event.clientX - rect.left) / scale;
      const y = (event.clientY - rect.top) / scale;

      if (isDrawingMode && currentDrawingPin) {
        // Check if rectangle is null or has zero dimensions
        const isValidRectangle = rectangle && rectangle.width > 0 && rectangle.height > 0;

        /**
         * Only allow creating a new rectangle if we don't have a valid one yet
         * or if the pin has no bounding box
         */
        if (!isValidRectangle || pinHasNoBoundingBox) {
          setRectangle({
            x,
            y,
            width: 100,
            height: 50,
          });
          setPinHasNoBoundingBox(false);
        }
      }
    },
    [isDrawingMode, scale, currentDrawingPin, rectangle, pinHasNoBoundingBox]
  );

  const handleRectangleChange = useCallback(
    async (newRect: { x: number; y: number; width: number; height: number }) => {
      setRectangle(newRect);

      // If we're not in drawing mode anymore and we have a callback, send the data
      if (!isDrawingMode && onDrawingComplete && rectangle) {
        const pageData = getCurrentPageData(fileData, pageNumber);

        if (pageData && pageData.width) {
          // Calculate the same scaling factor used in drawOnCanvas
          const scaleFactor = pdfWidth / pageData.width;

          // Convert coordinates back to original PDF scale
          const boundingBox = {
            leftmost: Math.round(newRect.x / scaleFactor),
            rightmost: Math.round((newRect.x + newRect.width) / scaleFactor),
            topmost: Math.round(newRect.y / scaleFactor),
            bottommost: Math.round((newRect.y + newRect.height) / scaleFactor),
          };

          handleDrawingComplete(boundingBox);
          setRectangle(null); // Clear the rectangle after sending
        }
      }
    },
    [isDrawingMode, onDrawingComplete, rectangle, fileData, pageNumber, pdfWidth, currentDrawingPin]
  );

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const changePage = useCallback(
    async (newPage: number) => {
      if (newPage >= 1 && newPage <= (numPages || 1)) {
        setPageNumber(newPage);
        clearCanvas(canvasRef);

        navigate({
          to: `/file/${fileId}`,
          search: { page: newPage },
        });

        await onPageChange(newPage);
      }
    },
    [numPages, navigate, fileId, onPageChange, setPageNumber]
  );

  const handleZoom = useCallback((delta: number) => {
    setScale((prevScale) => {
      const newScale = Math.max(1, Math.min(prevScale + delta, 3));
      return newScale;
    });
  }, []);

  const handleDrawingComplete = (boundingBox: any) => {
    if (onDrawingComplete && currentDrawingPin) {
      onDrawingComplete(boundingBox, currentDrawingPin);
    }
  };

  return (
    <div className={'flex flex-col items-center gap-2'}>
      <div
        className={`relative overflow-auto max-w-full`}
        style={{ width: `${pdfWidth}px`, height: `${pdfHeight}px` }}
        ref={containerRef}
      >
        <PageLoader visible={!fileData} />
        {file && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              width: `${pdfWidth}px`,
              height: `${pdfHeight}px`,
            }}
            onClick={handlePdfClick}
          >
            <Document file={file} onLoadSuccess={onDocumentLoadSuccess} loading={<PageLoader visible={true} />}>
              <Page
                pageNumber={pageNumber}
                width={pdfWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onLoadSuccess={updateCanvas}
              />
            </Document>
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: isDrawingMode ? 'auto' : 'none',
                width: '100%',
                height: '100%',
              }}
            />
            {rectangle && (
              <Rectangle
                shapeProps={rectangle}
                onChange={handleRectangleChange}
                parentBounds={{ width: pdfWidth, height: pdfHeight }}
              />
            )}
          </div>
        )}
      </div>
      <div className={'w-full flex items-center justify-between gap-2'}>
        <div className={'flex items-center gap-3'}>
          <Button
            className={'shadow-none gap-1'}
            icon={<ChevronLeft size={24} />}
            text={true}
            onClick={() => changePage(pageNumber - 1)}
            disabled={pageNumber <= 1}
          >
            Předchozí
          </Button>
          <div className={'flex items-center gap-2.5'}>
            <InputNumber
              className={'numberInput'}
              name={'pageNumber'}
              value={pageNumber}
              max={numPages!}
              min={1}
              onChange={(e: any) => {
                const newPage = Math.min(e.value, numPages!);
                if (newPage !== pageNumber) {
                  changePage(newPage);
                }
              }}
            />
            z {numPages}
          </div>
          <Button
            className={'shadow-none gap-1'}
            text={true}
            onClick={() => changePage(pageNumber + 1)}
            disabled={pageNumber >= (numPages || 1)}
          >
            <span className="flex items-center gap-1">
              Další
              <ChevronRight size={24} />
            </span>
          </Button>
        </div>
        <div className={'flex items-center gap-1'}>
          <Button
            className={'shadow-none gap-1'}
            icon={<ZoomOut size={24} />}
            text={true}
            onClick={() => handleZoom(-0.2)}
            disabled={scale === 1}
          >
            Oddálit
          </Button>
          <Button
            className={'shadow-none gap-1'}
            icon={<ZoomIn size={24} />}
            text={true}
            onClick={() => handleZoom(0.2)}
            disabled={scale === 3}
          >
            Přiblížit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
